import { Component, Inject } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../ng-store/index';
import * as articleActions from '../../ng-store/action/article.action';
import { Subject, takeUntil } from 'rxjs';
import { ArticleModel } from '../../ng-store/model/article.model';
import { Breadcrumb } from 'src/app/page-header/component/page-header.component';
import { Meta, Title } from '@angular/platform-browser';
import { TemplatePageTitleStrategy } from 'src/app/strategy/template-page-title-strategy';
import {trackArticle} from "../../ng-store/action/track.action";
import { PageHeaderComponent } from '../../../page-header/component/page-header.component';
import { SectionContactComponent } from '../../../contact/component/section-contact/section-contact.component';
import { MarkdownModule } from 'ngx-markdown';
import { NgFor, NgIf } from '@angular/common';
import { BlogService } from '../../../section-blog/ng-store/service/blog.service';

@Component({
    selector: 'app-article',
    templateUrl: './article.component.html',
    styleUrls: ['./article.component.scss'],
    standalone: true,
    imports: [PageHeaderComponent, SectionContactComponent, MarkdownModule, RouterLink, NgFor, NgIf],
    providers: [BlogService]
})
export class ArticleComponent {
  destroy$: Subject<boolean> = new Subject<boolean>();
  article = {} as ArticleModel;
  heading = '';
  description: Array<string> = [];
  breadcrumbs: Array<Breadcrumb> = [];
  markdown = '';
  otherArticles: ArticleModel[] = [];
  allArticles: ArticleModel[] = [];
  private blogLoaded = false;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly store: Store,
    private readonly meta: Meta,
    private readonly titleStrategy: TemplatePageTitleStrategy,
    private readonly blogService: BlogService
  ) {
    this
      .store
      .select(fromRoot.articleSelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe(articleState => {
        if (articleState.isDownloadingSuccess) {
          this.article = articleState.article;
          this.breadcrumbs = [{
            link: '/home',
            ariaLabel: 'Go back to home page',
            label: 'Home'
          }, {
            link: '/blog',
            ariaLabel: 'Blog',
            label: 'Blog'
          }, {
            link: '',
            ariaLabel: '',
            label: this.article.category.S
          }];
          this.heading = this.article.title.S;
          this.description = [this.article.description.S];
          this.markdown = this.article.body.S;

          const baseUrl = 'https://1artifactware.com/';
          const imageUrl = this.article.thumbnail?.S 
            ? (this.article.thumbnail.S.startsWith('http') ? this.article.thumbnail.S : `${baseUrl}${this.article.thumbnail.S}`)
            : `${baseUrl}assets/blog/img4.webp`;

          this.meta.updateTag({ name: 'description', content: this.article.description.S });
          this.meta.updateTag({ name: 'twitter:description', content: this.article.description.S });
          this.meta.updateTag({ property: 'og:description', content: this.article.description.S });

          this.meta.updateTag({ name: 'twitter:title', content: this.article.title.S });
          this.meta.updateTag({ property: 'og:title', content: this.article.title.S });
          this.meta.updateTag({ property: 'og:type', content: 'article' });
          this.meta.updateTag({ property: 'og:site_name', content: '1artifactware' });
          
          // Set dynamic title through TemplatePageTitleStrategy
          this.titleStrategy.setDynamicTitle(this.article.title.S);

          this.meta.updateTag({ property: 'og:url', content: `https://1artifactware.com/blog/article/${this.article.slug.S}` });
          this.meta.updateTag({ name: 'twitter:image', content: imageUrl });
          this.meta.updateTag({ property: 'og:image', content: imageUrl });
          this.meta.updateTag({ property: 'og:image:width', content: '1200' });
          this.meta.updateTag({ property: 'og:image:height', content: '630' });
          this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });

          // Load related articles
          this.loadRelatedArticles();
        }
        if (articleState.isDownloadingFailure && articleState.error == '404') {
          // Don't navigate during prerendering - let the resolver handle it
          if (typeof window !== 'undefined') {
            this.router.navigate(['/page-not-found']);
          }
        } else if (articleState.isDownloadingFailure) {
        }
      });
    const slug = this.route.snapshot.paramMap.get('slug');
    if (slug) {
      this.store.dispatch(articleActions.article({ slug: slug }));
    }
    if (this.route.snapshot.queryParamMap.has('resource')) {
      const resource = this.route.snapshot.queryParamMap.get('resource');
      if (resource) {
        this
          .store
          .dispatch(trackArticle({resource}));
      }
    }
  }

  private loadRelatedArticles(): void {
    if (this.blogLoaded) return;
    this.blogLoaded = true;

    this.blogService.blog().subscribe({
      next: (blogData) => {
        this.allArticles = blogData.Items;
        this.filterRelatedArticles();
      },
      error: () => {
        // Silently fail - other articles section won't show
      }
    });
  }

  private filterRelatedArticles(): void {
    const relatedSlugs = this.article.related_articles?.L || [];
    
    if (relatedSlugs.length === 0) {
      this.otherArticles = [];
      return;
    }

    const slugStrings = relatedSlugs.map(s => s.S);
    
    this.otherArticles = this.allArticles.filter(
      article => article.slug?.S && slugStrings.includes(article.slug.S)
    );
  }
}
